/**
 * SpatialAudioManager - Web Audio API based spatial audio system
 *
 * Features:
 * - Distance-based volume attenuation
 * - Stereo panning based on relative position
 * - Sound preloading and caching
 * - Configurable distance models
 */
import { ClientLogger } from '../logger/ClientLogger.js';

const spatialAudioLoggerFallback = (() => {
  if (typeof window !== 'undefined' && window.console) {
    return window.console;
  }
  return { log: () => {}, warn: () => {}, error: () => {}, info: () => {} };
})();
const spatialAudioLogger = ClientLogger.getLogger('audio') || spatialAudioLoggerFallback;

class SpatialAudioManager {
  constructor(config = {}) {
    this.audioContext = null;
    this.masterGain = null;
    this.listener = { x: 0, y: 0 };
    this.soundBuffers = new Map();  // path -> AudioBuffer
    this.activeSounds = new Set();
    this.initialized = false;

    // Configuration with defaults
    this.config = {
      maxDistance: config.maxDistance || 1200,      // Beyond this, sound is silent
      refDistance: config.refDistance || 100,       // Distance at which volume is 100%
      rolloffFactor: config.rolloffFactor || 1.5,   // How quickly sound fades
      distanceModel: config.distanceModel || 'exponential', // 'linear', 'inverse', 'exponential'
      masterVolume: config.masterVolume || 1.0,
      ...config
    };
  }

  /**
   * Initialize AudioContext (call on first user interaction)
   */
  async init() {
    if (this.initialized) return;

    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();

      // Create master gain node
      this.masterGain = this.audioContext.createGain();
      this.masterGain.gain.value = this.config.masterVolume;
      this.masterGain.connect(this.audioContext.destination);

      this.initialized = true;
      spatialAudioLogger.info('[SpatialAudio] Initialized');
    } catch (err) {
      spatialAudioLogger.error('[SpatialAudio] Failed to initialize:', err);
    }
  }

  /**
   * Resume audio context (required after user interaction in some browsers)
   */
  async resume() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
      spatialAudioLogger.info('[SpatialAudio] Resumed');
    }
  }

  /**
   * Preload multiple sounds for instant playback
   * @param {string[]} soundPaths - Array of sound file paths
   */
  async preload(soundPaths) {
    if (!this.initialized) await this.init();

    const loadPromises = soundPaths.map(path => this._loadSound(path));
    await Promise.all(loadPromises);

    spatialAudioLogger.debug(`[SpatialAudio] Preloaded ${soundPaths.length} sounds`);
  }

  /**
   * Load and decode a single sound file
   * @private
   */
  async _loadSound(soundPath) {
    if (this.soundBuffers.has(soundPath)) {
      return this.soundBuffers.get(soundPath);
    }

    try {
      const response = await fetch(soundPath);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      this.soundBuffers.set(soundPath, audioBuffer);
      return audioBuffer;
    } catch (err) {
      spatialAudioLogger.warn(`[SpatialAudio] Failed to load: ${soundPath}`, err);
      return null;
    }
  }

  /**
   * Play a sound at a world position with spatial audio
   * @param {string} soundPath - Path to sound file
   * @param {number} x - World X position
   * @param {number} y - World Y position
   * @param {Object} options - Optional overrides
   * @returns {AudioBufferSourceNode|null}
   */
  async playAt(soundPath, x, y, options = {}) {
    if (!this.initialized) await this.init();

    const spatial = this._calculateSpatialParams(x, y, options.config);

    // Cull sounds that are too far away
    if (spatial.culled) {
      return null;
    }

    // Get or load the audio buffer
    let buffer = this.soundBuffers.get(soundPath);
    if (!buffer) {
      buffer = await this._loadSound(soundPath);
      if (!buffer) return null;
    }

    // Create audio nodes
    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;

    // Gain node for volume
    const gainNode = this.audioContext.createGain();
    // `??`, not `||`: a caller-supplied volume of 0 means silent, not "unset".
    gainNode.gain.value = spatial.volume * (options.volume ?? 1.0);

    // Stereo panner for left/right positioning
    const pannerNode = this.audioContext.createStereoPanner();
    pannerNode.pan.value = spatial.pan;

    // Connect: source -> gain -> panner -> master -> destination
    source.connect(gainNode);
    gainNode.connect(pannerNode);
    pannerNode.connect(this.masterGain);

    // Track active sounds for cleanup
    this.activeSounds.add(source);
    source.onended = () => this.activeSounds.delete(source);

    // Play
    source.start(0);

    return source;
  }

  /**
   * Play a sound without spatialization (local player sounds, UI sounds)
   * @param {string} soundPath - Path to sound file
   * @param {Object} options - Optional settings
   * @returns {AudioBufferSourceNode|null}
   */
  async playLocal(soundPath, options = {}) {
    if (!this.initialized) await this.init();

    // Get or load the audio buffer
    let buffer = this.soundBuffers.get(soundPath);
    if (!buffer) {
      buffer = await this._loadSound(soundPath);
      if (!buffer) return null;
    }

    // Create audio nodes
    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;

    // Gain node for volume
    const gainNode = this.audioContext.createGain();
    gainNode.gain.value = options.volume || 1.0;

    // Connect: source -> gain -> master -> destination
    source.connect(gainNode);
    gainNode.connect(this.masterGain);

    // Track active sounds for cleanup
    this.activeSounds.add(source);
    source.onended = () => this.activeSounds.delete(source);

    // Play
    source.start(0);

    return source;
  }

  /**
   * Play a looping local sound (no spatialization). Sample-accurate: HTML
   * Audio `loop` inserts a gap at each restart, which is audible on held
   * movement loops like footsteps.
   * @param {string} soundPath - Path to sound file
   * @param {Object} options - Optional settings (`volume`, `playbackRate`)
   * @returns {{ source: AudioBufferSourceNode, gainNode: GainNode }|null}
   */
  async playLoopingLocal(soundPath, options = {}) {
    if (!this.initialized) await this.init();
    await this.resume();

    let buffer = this.soundBuffers.get(soundPath);
    if (!buffer) {
      buffer = await this._loadSound(soundPath);
      if (!buffer) return null;
    }

    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.playbackRate.value = options.playbackRate ?? 1.0;

    const gainNode = this.audioContext.createGain();
    gainNode.gain.value = options.volume ?? 1.0;

    source.connect(gainNode);
    gainNode.connect(this.masterGain);

    this.activeSounds.add(source);
    source.onended = () => this.activeSounds.delete(source);

    source.start(0);

    return { source, gainNode };
  }

  /**
   * Update listener position (local player position)
   * Call this every frame in the game loop
   * @param {number} x - Local player X position
   * @param {number} y - Local player Y position
   */
  updateListenerPosition(x, y) {
    this.listener.x = x;
    this.listener.y = y;
  }

  /**
   * Set master volume
   * @param {number} volume - Volume level (0-1)
   */
  setMasterVolume(volume) {
    this.config.masterVolume = Math.max(0, Math.min(1, volume));
    if (this.masterGain) {
      this.masterGain.gain.value = this.config.masterVolume;
    }
  }

  /**
   * Stop all currently playing sounds
   */
  stopAll() {
    for (const source of this.activeSounds) {
      try {
        source.stop();
      } catch (err) {
        // Already stopped or ended
      }
    }
    this.activeSounds.clear();
  }

  /**
   * Calculate volume and stereo pan based on sound position relative to listener
   * @param {number} soundX - Sound world X position
   * @param {number} soundY - Sound world Y position
   * @param {Object} configOverride - Optional config override for this sound
   * @returns {{ volume: number, pan: number, distance: number, culled: boolean }}
   * @private
   */
  _calculateSpatialParams(soundX, soundY, configOverride = null) {
    const config = configOverride || this.config;
    const dx = soundX - this.listener.x;
    const dy = soundY - this.listener.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // If beyond max distance, don't play
    if (distance > config.maxDistance) {
      return { volume: 0, pan: 0, distance, culled: true };
    }

    // Calculate volume based on distance model
    let volume = 1.0;
    const { refDistance, maxDistance, rolloffFactor, distanceModel } = config;

    if (distance > refDistance) {
      switch (distanceModel) {
        case 'linear':
          // Linear falloff: 1 at refDistance, 0 at maxDistance
          volume = 1 - rolloffFactor * (distance - refDistance) / (maxDistance - refDistance);
          break;
        case 'inverse':
          // Inverse falloff: realistic but gentle
          volume = refDistance / (refDistance + rolloffFactor * (distance - refDistance));
          break;
        case 'exponential':
          // Exponential falloff: more dramatic, good for games
          volume = Math.pow(distance / refDistance, -rolloffFactor);
          break;
      }
    }

    volume = Math.max(0, Math.min(1, volume));

    // Calculate stereo pan (-1 = left, 0 = center, 1 = right)
    // Use angle from listener to sound
    const angle = Math.atan2(dx, -dy); // Y-down coordinate system
    const pan = Math.sin(angle); // Simple left/right panning

    return { volume, pan, distance, culled: false };
  }
}

export { spatialAudioLoggerFallback, spatialAudioLogger, SpatialAudioManager };
